import {inject, Injectable} from '@angular/core';
import {WorkspaceInterface} from "../interfaces/workspace.interface";
import {BehaviorSubject, combineLatest, filter, first, firstValueFrom, map, Observable, take} from "rxjs";
import {AuthService} from "./auth.service";
import {Router} from "@angular/router";
import {WorkflowRunInterface} from "../interfaces/workflow/workflow-run.interface";
import {KeysInterface} from "../interfaces/keys.interface";
import {RolesType} from "../interfaces/user/roles.type";
import {
  ALL_SECTIONS,
  MemberPermissions,
  PermissionLevel,
  PermissionSection,
  ResolvedPermissions,
  resolvePermissions,
  sectionRole
} from "../interfaces/user/permissions.type";
import {addDays, addMonths, endOfDay, format, startOfDay, subDays} from "date-fns";
import {HelperService} from "./helpers/helper.service";
import {
  average,
  collection,
  count,
  deleteField,
  doc,
  Firestore,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  sum,
  updateDoc,
  where
} from "@angular/fire/firestore";
import {TierInterface} from "../interfaces/billing/tier.Interface";
import {AdditionalCostsInterface} from "../interfaces/workspace/additional-costs.interface";
import {DayRange} from "../interfaces/workspace/date-range.interface";
import {DailyStatInterface} from "../interfaces/workspace/daily-stat.interface";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {DashboardInterface, DashboardWidgetInstance} from "../interfaces/track/dashboard.interface";

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private workspaces = new BehaviorSubject<WorkspaceInterface[]>([]);
  workspaces$ = this.workspaces.asObservable();
  public workspaceId: string // Set in navigation component
  public workspace = new BehaviorSubject<WorkspaceInterface | undefined>(undefined);
  selectedWorkspace: WorkspaceInterface | undefined = undefined;
  workspace$ = this.workspace.asObservable();
  private role = new BehaviorSubject<RolesType>('viewer');
  role$ = this.role.asObservable();
  // null until the workspace doc has been read — templates and the guard wait on that
  // rather than flashing every section and then hiding half of them.
  private permissions = new BehaviorSubject<ResolvedPermissions | null>(null);
  permissions$ = this.permissions.asObservable();
  public roleSet = false
  /**
   * The legacy role shape, resolved per section: an admin stays 'admin', a member with write
   * on that section reads as 'editor', anyone else 'viewer'. Lets the existing
   * `role !== 'viewer'` templates honour the permissions map. New templates use the `can` pipe.
   */
  roleIn: Record<PermissionSection, Observable<RolesType>> = ALL_SECTIONS.reduce((acc, section) => {
    acc[section.key] = combineLatest([this.role$, this.permissions$]).pipe(
      map(([role, permissions]) => sectionRole(role, permissions?.[section.key]))
    )
    return acc
  }, {} as Record<PermissionSection, Observable<RolesType>>)
  private inviteWatcherUnsub: (() => void) | null = null

  constructor(
    private authSvc: AuthService,
    private router: Router,
    private helperSvc: HelperService,
    private firestore: Firestore,
    private http: HttpClient,
  ) {
    this.authSvc.userData$.subscribe(async (userData) => {
      if (userData) {
        const workspaces = await this.getAllWorkspaces(userData.uid)
        if (workspaces.length === 0) {
          console.log('Redirecting to home because no workspaces found')
          if (!userData.onBoarded) {
            await this.router.navigate(['/on-boarding'])
            this.watchInvitedWorkspaces(userData.uid)
          } else {
            await this.router.navigate(['/home'])
          }
          return
        }
        const urlId = this.getWorkspaceIdFromUrl()
        let id = (urlId && workspaces.find(x => x.id === urlId))
          ? urlId
          : localStorage.getItem('workspaceId')
        if (!id || !workspaces.find(x => x.id === id)) {
          id = workspaces[0].id
        }
        localStorage.setItem('workspaceId', id)
        this.workspaceId = id
        if (!this.roleSet) {
          await this.getRole(null, '', id)
        }
      } else {
        this.stopInviteWatcher()
        this.workspace.next(undefined)
        this.role.next('')
        this.permissions.next(null)
        this.workspaces.next([])
        this.selectedWorkspace = undefined
        this.workspaceId = ''
        this.roleSet = false
      }
    })
  }

  /** Synchronous read for guards/services; templates use the `can` pipe on permissions$. */
  can(section: PermissionSection, level: PermissionLevel = 'read'): boolean {
    const current = this.permissions.value?.[section] ?? 'none'
    return (level === 'write') ? current === 'write' : current !== 'none'
  }

  /** Permissions are per workspace — re-read them whenever the routed workspace changed. */
  async ensureRole(workspaceId: string) {
    if (this.selectedWorkspace?.id === workspaceId && this.permissions.value) { return }
    await this.getRole(null, '', workspaceId)
  }

  async waitForPermissions(): Promise<ResolvedPermissions> {
    return firstValueFrom(this.permissions$.pipe(filter((p): p is ResolvedPermissions => !!p), first()))
  }

  async setWorkspace(workspace: WorkspaceInterface) {
    this.workspaceId = workspace.id;
    await this.getAllWorkspaces(this.authSvc.uid)
    this.role.next('admin')
    this.permissions.next(resolvePermissions(workspace, this.authSvc.uid))
    this.selectedWorkspace = workspace
    this.workspace.next(workspace)
    localStorage.setItem('workspaceId', workspace.id)
    this.roleSet = true
  }

  async getAllWorkspaces(uid: string) {
    const q = query(
      collection(this.firestore, 'workspaces'),
      where('users', 'array-contains', uid)
    )
    const snapshot = await getDocs(q)
    const workspaces = snapshot.docs.map((doc) => ({
      ...(doc.data() as WorkspaceInterface),
      id: doc.id
    }));
    this.workspaces.next(workspaces);
    return workspaces;
  }

  async getWorkspace(workspaceId: string, setLocalStorage = false): Promise<WorkspaceInterface | null> {
    const ref = doc(this.firestore, 'workspaces', workspaceId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      const data = snap.data() as WorkspaceInterface
      data.id = snap.id
      if (setLocalStorage) {
        localStorage.setItem('workspaceId', snap.id)
        localStorage.setItem('lastWorkspaceUsed', snap.id)
      }
      return data
    } else {
      return null
    }
  }

  async getKeys(workspaceId: string): Promise<KeysInterface | null> {
    const ref = doc(this.firestore, 'keys', workspaceId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      return snap.data() as KeysInterface
    } else {
      return null
    }
  }

  async changeAuthToken(workspaceId: string) {
    const newToken = this.helperSvc.generateId(25)
    const ref = doc(this.firestore, 'keys', workspaceId)
    await updateDoc(ref, {
      trigger: newToken
    })
    return newToken
  }

  async getRole(workspace: WorkspaceInterface | null, uid: string, workspaceId: string = ''): Promise<RolesType> {
    if (!workspace) {
      workspace = await this.getWorkspace(workspaceId) as WorkspaceInterface
    }
    this.workspace.next(workspace)
    this.selectedWorkspace = workspace
    if (!uid) {
      const user = await firstValueFrom(this.authSvc.user$)
      uid = user?.uid ?? ''
    }
    if (!uid) {
      console.error('Could not get role for workspace', workspace.id)
    }
    this.permissions.next(resolvePermissions(workspace, uid))
    if (workspace.admins.indexOf(uid) !== -1) {
      this.role.next('admin')
      return 'admin'
    } else if (workspace.editors.indexOf(uid) !== -1) {
      this.role.next('editor')
      return 'editor'
    } else {
      this.role.next('viewer')
      return 'viewer'
    }
  }

  /**
   * `router.url` is only the *last completed* navigation, so on a hard refresh it is still '/'
   * while the auth and permission guards await Firebase — anything reading it in that window got
   * nothing and fell back to a possibly stale localStorage id (the nav then kept showing the
   * previous workspace's name). `location.pathname` is the real URL from the first frame, so it
   * is the authority whenever the router hasn't caught up. An explicit `url` argument is taken
   * as given. Never blanks `workspaceId` on a non-workspace route.
   */
  getWorkspaceIdFromUrl(url: string = ''): string {
    const fromUrl = (path: string) => {
      const split = path.split('/')
      return split[1] === 'workspace' ? (split[2] ?? '') : ''
    }
    const workspaceId = fromUrl(url || this.router.url) || (url ? '' : fromUrl(location.pathname))
    if (workspaceId) { this.workspaceId = workspaceId }
    return workspaceId
  }

  async createWorkspace(workspace: WorkspaceInterface, redirect = true) {
    const freeTierRef = doc(this.firestore, 'tiers', '0')
    const snap = await getDoc(freeTierRef)
    const freeTier = snap.data() as TierInterface

    workspace.meta = {
      createdAt: new Date().getTime(),
      createdBy: this.authSvc.uid,
      lastUpdate: -1,
      lastUpdateBy: ''
    }
    workspace.admins = [this.authSvc.uid]
    workspace.users = [this.authSvc.uid]
    workspace.editors = [this.authSvc.uid]
    workspace.seats = freeTier?.seats ?? 2
    workspace.tier = 0
    workspace.successfulRuns = 0
    workspace.totalRuns = 0
    workspace.maxRuns = freeTier?.workflowRuns ?? 200
    workspace.historyTTL = freeTier?.historyDays ?? 3
    workspace.triggerAuth = false
    workspace.currentPeriodRuns = 0
    workspace.maxWorkflows = freeTier?.workflows ?? 2
    workspace.maxIntegrations = freeTier?.integrations ?? 5
    workspace.extraRunCost = 0.1
    workspace.runOverflow = false
    workspace.periodStart = new Date().getTime()
    workspace.periodEnd = addMonths(new Date(), 1).getTime()
    workspace.createdAt = new Date().getTime()
    workspace.additionalCost = 0
    workspace.credits = 0
    workspace.totalCreditsPurchased = 0
    workspace.maxTier = 0
    const ref = doc(this.firestore, 'workspaces', workspace.id)
    await setDoc(ref, workspace)
    this.workspaceId = workspace.id
    this.workspaces.next(await this.getAllWorkspaces(this.authSvc.uid))
    await this.getRole(workspace, this.authSvc.uid)
    if (redirect) {
      await this.router.navigate(['/workspace', workspace.id, 'workflows'])
    }
  }

  async updateWorkspace(workspace: WorkspaceInterface) {
    workspace.meta.lastUpdateBy = this.authSvc.uid
    workspace.meta.lastUpdate = new Date().getTime()
    const ref = doc(this.firestore, 'workspaces', workspace.id)
    await updateDoc(ref, {
      name: workspace.name,
      description: workspace.description,
      logoUrl: workspace.logoUrl ?? '',
      triggerAuth: workspace.triggerAuth,
      meta: workspace.meta,
    })
  }

  /**
   * `memberUid` writes that member's permissions with a dotted field path so two admins
   * editing different members can't clobber each other's map. `null` permissions removes them.
   */
  async updateWorkspaceMembers(workspace: WorkspaceInterface, memberUid = '', permissions?: MemberPermissions | null) {
    workspace.meta.lastUpdateBy = this.authSvc.uid
    workspace.meta.lastUpdate = new Date().getTime()
    const ref = doc(this.firestore, 'workspaces', workspace.id)
    const payload: {[key: string]: any} = {
      admins: workspace.admins,
      editors: workspace.editors,
      users: workspace.users
    }
    if (memberUid && permissions !== undefined) {
      payload[`permissions.${memberUid}`] = permissions ?? deleteField()
      // Keep the caller's copy in sync — the team page re-reads it to redraw the access column.
      if (permissions) {
        workspace.permissions = { ...(workspace.permissions ?? {}), [memberUid]: permissions }
      } else if (workspace.permissions) {
        delete workspace.permissions[memberUid]
      }
    }
    await updateDoc(ref, payload)
  }

  async getLatestRuns(workspaceId: string, limitDocs = 20, workflowIds?: string[]) {
    if (workflowIds && workflowIds.length === 0) { return [] }
    const q = query(
      collection(this.firestore, 'runs'),
      where('workspaceId', '==', workspaceId),
      ...this.workflowIdsConstraint(workflowIds),
      orderBy('start', 'desc'),
      limit(limitDocs)
    )
    const snap = await getDocs(q)
    return snap.docs.map((doc) => {
      const data = doc.data() as WorkflowRunInterface;
      data.id = doc.id;
      return data;
    });
  }

  /**
   * One `limit(1)` query per workflow, run in parallel, rather than a single shared
   * `getLatestRuns` window — a shared top-N-across-all-workflows query pushes out any workflow
   * that simply hasn't run as recently/often as its siblings, even though it does have runs, so
   * callers that need "does this specific workflow have a last run" (e.g. the engine detail
   * list) must ask per workflow instead of scanning a shared recent-activity feed.
   */
  async getLastRunPerWorkflow(workspaceId: string, workflowIds: string[]): Promise<Map<string, WorkflowRunInterface>> {
    const runs = await Promise.all(workflowIds.map(async (workflowId) => {
      const q = query(
        collection(this.firestore, 'runs'),
        where('workspaceId', '==', workspaceId),
        where('workflowId', '==', workflowId),
        orderBy('start', 'desc'),
        limit(1)
      )
      const snap = await getDocs(q)
      if (snap.empty) { return null }
      const data = snap.docs[0].data() as WorkflowRunInterface
      data.id = snap.docs[0].id
      return data
    }))
    const map = new Map<string, WorkflowRunInterface>()
    runs.forEach((run) => {
      if (run) { map.set(run.workflowId, run) }
    })
    return map
  }

  /**
   * Per-workflow run count and credit spend over the last `days`.
   *
   * One aggregate query per workflow, in parallel — the `in` based aggregates used by
   * `totalTasks` can only return a single collapsed total for the whole set, so a per-row
   * breakdown has to ask per workflow. Same field path as `getLastRunPerWorkflow`
   * (`workspaceId + workflowId + start`), so no additional composite index is needed.
   */
  async getUsagePerWorkflow(workspaceId: string, days: number, workflowIds: string[]): Promise<Map<string, {runs: number, credits: number}>> {
    const dateLimit = addDays(new Date(), -days).getTime()
    const usage = await Promise.all(workflowIds.map(async (workflowId) => {
      const q = query(
        collection(this.firestore, 'runs'),
        where('workspaceId', '==', workspaceId),
        where('workflowId', '==', workflowId),
        where('start', '>', dateLimit)
      )
      const snap = await getAggregateFromServer(q, {
        runs: count(),
        credits: sum('additionalCost')
      })
      const data = snap.data()
      return {workflowId, runs: data.runs ?? 0, credits: data.credits ?? 0}
    }))
    const map = new Map<string, {runs: number, credits: number}>()
    usage.forEach((entry) => map.set(entry.workflowId, {runs: entry.runs, credits: entry.credits}))
    return map
  }

  /** Caps at Firestore's 30-value limit for an `in` filter — real engines are expected to stay well under this. */
  private workflowIdsConstraint(workflowIds?: string[]) {
    if (!workflowIds) { return [] }
    if (workflowIds.length > 30) {
      console.warn(`workflowIds (${workflowIds.length}) exceeds Firestore's 'in' cap of 30, truncating`)
    }
    return [where('workflowId', 'in', workflowIds.slice(0, 30))]
  }

  async getWorkflowRun(workspaceId: string, runId: string) {
    const ref = doc(this.firestore, 'runs', runId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      const data = snap.data() as WorkflowRunInterface
      data.id = snap.id
      return data
    } else {
      return null
    }
  }


  /**
   * Two roles: an admin bypasses every section check (so their stored map would only go stale
   * and is dropped), a member is governed entirely by `permissions`.
   */
  async setMemberAccess(uid: string, isAdmin: boolean, workspace: WorkspaceInterface, permissions?: MemberPermissions) {
    if (isAdmin) {
      workspace.editors = this.helperSvc._addArrayElement(workspace.editors, uid)
      workspace.admins = this.helperSvc._addArrayElement(workspace.admins, uid)
    } else {
      workspace.admins = this.helperSvc._deleteArrayElement(workspace.admins, uid)
    }
    await this.updateWorkspaceMembers(workspace, uid, isAdmin ? null : (permissions ?? {}))
  }

  async changeWorkspaceMemberRole(uid: string, newRole: string, workspace: WorkspaceInterface) {
    switch (newRole) {
      case 'read':
        workspace.editors = this.helperSvc._deleteArrayElement(workspace.editors, uid)
        workspace.admins = this.helperSvc._deleteArrayElement(workspace.admins, uid)
        break;
      case 'edit':
        workspace.editors = this.helperSvc._addArrayElement(workspace.editors, uid)
        workspace.admins = this.helperSvc._deleteArrayElement(workspace.admins, uid)
        break;
      case 'admin':
        workspace.editors = this.helperSvc._addArrayElement(workspace.editors, uid)
        workspace.admins = this.helperSvc._addArrayElement(workspace.admins, uid)
        break;
    }
    await this.updateWorkspaceMembers(workspace)
  }

  async addWorkspaceMember(newUid: string, newRole: string, workspace: WorkspaceInterface) {
    workspace.users = this.helperSvc._addArrayElement(workspace.users, newUid)
    if (newRole === 'edit' || newRole === 'admin') {
      workspace.editors = this.helperSvc._addArrayElement(workspace.editors, newUid)
    }
    if (newRole === 'admin') {
      workspace.admins = this.helperSvc._addArrayElement(workspace.admins, newUid)
    }
    await this.updateWorkspaceMembers(workspace)
  }

  async deleteWorkspaceMember(uid: string, workspace: WorkspaceInterface) {
    workspace.users = this.helperSvc._deleteArrayElement(workspace.users, uid)
    workspace.editors = this.helperSvc._deleteArrayElement(workspace.editors, uid)
    workspace.admins = this.helperSvc._deleteArrayElement(workspace.admins, uid)
    await this.updateWorkspaceMembers(workspace, uid, null)
  }


  async getWorkspaceRunsCount(workspaceId: string, limitDate: number) {
    const q = query(
      collection(this.firestore, 'runs'),
      where('workspaceId', '==', workspaceId),
      where('start', '>', limitDate)
    )
    const snap = await getCountFromServer(q)
    return snap.data().count
  }

  async getWorkspaceQuotaUsage(workspaceId: string, periodStart: number) {
    try {
      const response = await firstValueFrom(this.http.get(`${environment.core}workspace/quota/${workspaceId}?period_start=${periodStart}`)) as number
      console.log('Quota response', response)
      return response
    } catch (e) {
      // Fallback to count documents
      return await this.getWorkspaceRunsCount(workspaceId, periodStart)
    }
  }

  async dailyActivity(workspaceId: string, days: number, workflowIds?: string[]) {
    const range = (days: number) => {
      const ranges: DayRange[] = [];
      for (let i = 0; i < days; i++) {
        const day = subDays(new Date(), i);
        ranges.push({
          start: startOfDay(day).getTime(),
          end: endOfDay(day).getTime(),
        });
      }
      return ranges;
    }

    if (workflowIds && workflowIds.length === 0) {
      return range(days).map((day) => ({
        day: day.start,
        runs: 0,
        additionalCost: 0,
        issues: 0,
        dayString: format(new Date(day.start), "MMM do"),
      })) as DailyStatInterface[]
    }

    const requests = range(days).map(async (day) => {
      const runsQuery = query(
        collection(this.firestore, "runs"),
        where("workspaceId", "==", workspaceId),
        ...this.workflowIdsConstraint(workflowIds),
        where("start", ">", day.start),
        where("start", "<", day.end)
      );

      const errorsQuery = query(
        collection(this.firestore, "runs"),
        where("workspaceId", "==", workspaceId),
        ...this.workflowIdsConstraint(workflowIds),
        where("start", ">", day.start),
        where("start", "<", day.end),
        where("error", "==", true)
      );

      const [runsSnapshot, errorsSnapshot] = await Promise.all([
        getAggregateFromServer(runsQuery, {
          runs: count(),
          additionalCost: sum("additionalCost"),
        }),
        getAggregateFromServer(errorsQuery, {
          issues: count(),
        }),
      ]);

      return {
        day: day.start,
        runs: runsSnapshot.data().runs,
        additionalCost: parseFloat(runsSnapshot.data().additionalCost.toFixed(4)),
        issues: errorsSnapshot.data().issues,
        dayString: format(new Date(day.start), "MMM do"),
      };
    });

    // Wait for all requests to complete
    return await Promise.all(requests) as DailyStatInterface[]
  }

  async getWorkspaceSuccessfulRunsCount(workspaceId: string, lastNDays = 30) {
    const limitDate = addDays(new Date(), -lastNDays)
    const q = query(
      collection(this.firestore, 'runs'),
      where("workspaceId", "==", workspaceId),
      where('error', '==', false),
      where('start', '>', limitDate.getTime())
    )
    const snap = await getCountFromServer(q)
    return snap.data().count
  }

  private stopInviteWatcher() {
    if (this.inviteWatcherUnsub) {
      this.inviteWatcherUnsub()
      this.inviteWatcherUnsub = null
    }
  }

  private watchInvitedWorkspaces(uid: string) {
    this.stopInviteWatcher()
    const q = query(
      collection(this.firestore, 'workspaces'),
      where('users', 'array-contains', uid)
    )
    this.inviteWatcherUnsub = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const workspaces = snapshot.docs.map((d) => ({
          ...(d.data() as WorkspaceInterface),
          id: d.id
        }))
        this.workspaces.next(workspaces)
        this.stopInviteWatcher()
      }
    })
    // Safety cleanup after 60s in case the CF never fires
    setTimeout(() => this.stopInviteWatcher(), 60_000)
  }

  /** `dashboardId` scopes the config — "results" is the one built-in dashboard today, but this
   *  shape (a subcollection doc per dashboard) is what lets custom dashboards be added later
   *  without a migration. */
  async getDashboard(workspaceId: string, dashboardId: string): Promise<DashboardInterface | null> {
    const ref = doc(this.firestore, 'workspaces', workspaceId, 'dashboards', dashboardId)
    const snap = await getDoc(ref)
    return snap.exists() ? snap.data() as DashboardInterface : null
  }

  async saveDashboardWidgets(workspaceId: string, dashboardId: string, widgets: DashboardWidgetInstance[], name: string, isBuiltIn: boolean) {
    const ref = doc(this.firestore, 'workspaces', workspaceId, 'dashboards', dashboardId)
    const dashboard: DashboardInterface = {id: dashboardId, name, isBuiltIn, widgets}
    await setDoc(ref, dashboard, {merge: true})
  }

  /** The dashboard-wide chart theme — every widget that hasn't overridden it follows this. */
  async saveDashboardTheme(workspaceId: string, dashboardId: string, theme: string) {
    const ref = doc(this.firestore, 'workspaces', workspaceId, 'dashboards', dashboardId)
    await setDoc(ref, {theme}, {merge: true})
  }

  async saveDashboardRange(workspaceId: string, dashboardId: string, rangeDays: number) {
    const ref = doc(this.firestore, 'workspaces', workspaceId, 'dashboards', dashboardId)
    await setDoc(ref, {rangeDays}, {merge: true})
  }

  async getAdditionalCost(workspaceId: string) {
    const ref = doc(this.firestore, 'additionalCosts', workspaceId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      return snap.data() as AdditionalCostsInterface
    } else {
      return null
    }
  }

  async totalTasks(workspaceId: string, days: number, workflowIds?: string[]) {
    if (workflowIds && workflowIds.length === 0) {
      return {
        runs: 0, failedTasks: 0, totalTasks: 0, avgTime: 0, avgTasksPerRun: 0,
        failed: 0, failRate: 0, successRate: 0, credits: 0,
        taskChange: 0, creditsChange: 0, errors: 0, warnings: 0,
      };
    }

    const dateLimit = addDays(new Date(), -days).getTime();
    const oldLimit = addDays(new Date(), -2 * days).getTime();

    const runsRef = collection(this.firestore, "runs");
    const workflowConstraint = this.workflowIdsConstraint(workflowIds)

    // prepare all queries
    const currentAggQuery = getAggregateFromServer(
      query(
        runsRef,
        where("workspaceId", "==", workspaceId),
        ...workflowConstraint,
        where("start", ">", dateLimit)
      ),
      {
        runs: count(),
        failedTasks: sum("tasks.totalFailed"),
        totalTasks: sum("tasks.total"),
        avgTime: average("seconds"),
        credits: sum("additionalCost"),
      }
    );

    const warningsQuery = getCountFromServer(
      query(
        runsRef,
        where("workspaceId", "==", workspaceId),
        ...workflowConstraint,
        where("start", ">", dateLimit),
        where("statusCode", "==", 206)
      )
    );

    const errorsQuery = getCountFromServer(
      query(
        runsRef,
        where("workspaceId", "==", workspaceId),
        ...workflowConstraint,
        where("start", ">", dateLimit),
        where("statusCode", ">", 206)
      )
    );

    const oldAggQuery = getAggregateFromServer(
      query(
        runsRef,
        where("workspaceId", "==", workspaceId),
        ...workflowConstraint,
        where("start", ">", oldLimit),
        where("start", "<=", dateLimit)
      ),
      {
        runs: count(),
        failedTasks: sum("tasks.totalFailed"),
        totalTasks: sum("tasks.total"),
        avgTime: average("seconds"),
        credits: sum("additionalCost"),
      }
    );

    // run everything in parallel
    const [agg, warnings, errors, oldAgg] = await Promise.all([
      currentAggQuery,
      warningsQuery,
      errorsQuery,
      oldAggQuery,
    ]);

    const safeDivide = (a: number, b: number) => (b === 0 ? 0 : a / b);

    const aggData = agg.data();
    const oldData = oldAgg.data();
    const warningsCount = warnings.data().count;
    const errorsCount = errors.data().count;

    console.log('errorsCount', errorsCount)
    console.log('warningsCount', warningsCount)
    console.log('aggData.runs', aggData.runs)

    return {
      runs: aggData.runs,
      failedTasks: aggData.failedTasks,
      totalTasks: aggData.totalTasks,
      avgTime: aggData.avgTime ?? 0,
      avgTasksPerRun: safeDivide(aggData.totalTasks, aggData.runs),
      failed: errorsCount + warningsCount,
      failRate: safeDivide(errorsCount + warningsCount, aggData.runs),
      successRate: safeDivide(aggData.runs - errorsCount - warningsCount, aggData.runs),
      credits: aggData.credits,
      taskChange: safeDivide(aggData.totalTasks, oldData.totalTasks),
      creditsChange: safeDivide(aggData.credits, oldData.credits),
      errors: errorsCount,
      warnings: warningsCount,
    };
  }
}
